import axios from 'axios';
import { API_BASE_URL } from './apiBaseUrl';
import { decrementLoading, incrementLoading } from './loadingStore';

// Configure axios defaults
axios.defaults.baseURL = API_BASE_URL;

// Track long-running requests (non-GET by default) for the global loader
axios.interceptors.request.use(
  (config) => {
    const method = String(config?.method || 'get').toLowerCase();
    const shouldTrack = !config?.skipGlobalLoader && (config?.showGlobalLoader || method !== 'get');
    if (shouldTrack) {
      incrementLoading();
      config.__globalLoading = true;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for token refresh
axios.interceptors.response.use(
  (response) => {
    if (response?.config?.__globalLoading) decrementLoading();
    return response;
  },
  async (error) => {
    if (error?.config?.__globalLoading) decrementLoading();
    if (error.response?.status === 401) {
      // Token expired, try to refresh
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const refreshResponse = await axios.post(
            '/auth/refresh',
            { refreshToken },
            { skipGlobalLoader: true }
          );
          const { accessToken, refreshToken: newRefreshToken } = refreshResponse.data;

          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

          // Retry the original request
          error.config.headers['Authorization'] = `Bearer ${accessToken}`;
          return axios(error.config);
        } catch (refreshError) {
          // Refresh failed, logout
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          delete axios.defaults.headers.common['Authorization'];
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (userData) => axios.post('/auth/register', userData),
  login: (credentials) => axios.post('/auth/login', credentials),
  logout: (refreshToken) => axios.post('/auth/logout', { refreshToken }),
  logoutAll: () => axios.post('/auth/logout-all'),
  refresh: (refreshToken) => axios.post('/auth/refresh', { refreshToken }),
  getMe: () => axios.get('/auth/me'),
  requestPasswordReset: (email) => axios.post('/auth/password-reset/request', { email }),
  verifyPasswordResetOtp: (email, otp) => axios.post('/auth/password-reset/verify', { email, otp }),
  resetPassword: (email, resetToken, newPassword) => axios.post('/auth/password-reset/reset', { email, resetToken, newPassword })
};

export const eventsAPI = {
  getEvents: (params, config = {}) => axios.get('/events', { params, ...config }),
  getCategories: (config = {}) => axios.get('/events/categories', config),
  createCategory: (data) => axios.post('/events/categories', data),
  updateCategory: (id, data) => axios.put(`/events/categories/${id}`, data),
  deleteCategory: (id, { deleteEvents = false } = {}) =>
    axios.delete(`/events/categories/${id}`, { data: { deleteEvents } }),
  createEvent: (eventData) => {
    const isFormData = typeof FormData !== 'undefined' && eventData instanceof FormData;
    // When sending FormData, do NOT set Content-Type explicitly so the browser
    // can add the correct multipart boundary header. Let axios handle the header.
    return isFormData ? axios.post('/events', eventData) : axios.post('/events', eventData);
  },
  updateEvent: (slug, eventData) => {
    const isFormData = typeof FormData !== 'undefined' && eventData instanceof FormData;
    return isFormData ? axios.put(`/events/${slug}`, eventData) : axios.put(`/events/${slug}`, eventData);
  },
  publishEvent: (slug) => axios.patch(`/events/${slug}/publish`),
  unpublishEvent: (slug) => axios.patch(`/events/${slug}/unpublish`),
  deleteEvent: (slug) => axios.delete(`/events/${slug}`),
  registerForEvent: (slug, data) => axios.post(`/events/${slug}/register`, data),
  getEventRegistrations: (params, config = {}) => axios.get('/events/registrations', { params, ...config }),
  approveRegistration: (id) => axios.patch(`/events/registrations/${id}/approve`),
  disapproveRegistration: (id) => axios.patch(`/events/registrations/${id}/disapprove`),
  deleteRegistration: (id) => axios.delete(`/events/registrations/${id}`)
};

export const usersAPI = {
  getAllUsers: () => axios.get('/users'),
  listUsers: (params, config = {}) => axios.get('/users', { params, ...config }),
  getUser: (id) => axios.get(`/users/${id}`),
  updateMyPhone: (phoneNumber) => axios.patch('/users/me/phone', { phone_number: phoneNumber }),
  getUserRoles: (id) => axios.get(`/users/${id}/roles`),
  grantRole: (id, role) => axios.post(`/users/${id}/roles`, { role }),
  revokeRole: (id, role) => axios.delete(`/users/${id}/roles`, { data: { role } }),
  deleteUser: (id) => axios.delete(`/users/${id}`)
  , getRenewalsDue: (days = 30) => axios.get('/users/renewals/due', { params: { days } })
  , sendRenewal: (userId, data) => axios.post(`/users/${userId}/renewals`, data)
};

export const residencesAPI = {
  getResidences: () => axios.get('/residences'),
  getCities: () => axios.get('/residences/cities'),
  getResidencesByCity: (city) => axios.get(`/residences/by-city/${encodeURIComponent(city)}`),
  createResidence: (residenceData) => axios.post('/residences', residenceData)
};

export const signupAPI = {
  createRequest: (formData) => axios.post('/signup', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  checkStatus: ({ email, ticketCode }) => axios.get('/signup/status', {
    params: { email, ticketCode }
  }),
  getRequests: (config = {}) => axios.get('/signup', config),
  getRequestById: (id) => axios.get(`/signup/admin/${id}`),
  getAttachments: (id) => axios.get(`/signup/${id}/attachments`),
  getTicketUser: (id) => axios.get(`/signup/${id}/user`),
  markAdminSeen: (id) => axios.post(`/signup/admin/${id}/seen`),
  approve: (id, data) => axios.put(`/signup/${id}/approve`, data),
  rejectRequest: (id) => axios.put(`/signup/admin/${id}/reject`),
  saveInvoiceDraft: (id, data) => axios.put(`/signup/admin/${id}/invoice`, data),
  sendInvoice: (id, data) => axios.post(`/signup/admin/${id}/invoice/send`, data),
  markInvoicePaid: (id) => axios.post(`/signup/admin/${id}/invoice/mark-paid`),
};

export const emailAPI = {
  sendTestEmail: (email) => axios.post('/email/test', { email }),
  sendMandateEmail: (email, redirectUrl) => axios.post('/email/mandate', { email, redirectUrl })
};

export const notificationsAPI = {
  getNotifications: (config = {}) => axios.get('/notifications', config),
  getUnreadCount: (config = {}) => axios.get('/notifications/unread-count', config),
  markAsRead: (id) => axios.put(`/notifications/${id}/read`),
  clearNotifications: () => axios.delete('/notifications'),
  clearSeenNotifications: () => axios.delete('/notifications/seen'),
  deleteNotification: (id) => axios.delete(`/notifications/${id}`),
  createNotification: (data) => axios.post('/notifications', data),
  broadcastNotification: (data) => axios.post('/notifications/broadcast', data),
  getSentNotifications: (params, config = {}) => axios.get('/notifications/sent', { params, ...config }),
  broadcastAdminNotification: (data) => axios.post('/notifications/broadcast-admin', data),
  getSentAdminNotifications: (params, config = {}) => axios.get('/notifications/sent-admin', { params, ...config }),
  getAdminRecipients: (config = {}) => axios.get('/notifications/admin-recipients', config),
  getAdminNotificationCategories: (config = {}) => axios.get('/notifications/admin-categories', config),
  getAdminCategoryRecipients: (categoryKey, config = {}) =>
    axios.get(`/notifications/admin-categories/${encodeURIComponent(categoryKey)}/recipients`, config),
  setAdminCategoryRecipientEnabled: ({ categoryKey, userId, enabled }) =>
    axios.patch(`/notifications/admin-categories/${encodeURIComponent(categoryKey)}/recipients/${userId}`, { enabled }),
  // Custom email management
  getCustomEmails: (config = {}) => axios.get('/notifications/custom-emails', config),
  addCustomEmail: (email) => axios.post('/notifications/custom-emails', { email }),
  deleteCustomEmail: (customEmailId) => axios.delete(`/notifications/custom-emails/${customEmailId}`),
  getCustomEmailsForCategory: (categoryKey, config = {}) =>
    axios.get(`/notifications/custom-emails/category/${encodeURIComponent(categoryKey)}`, config),
  setCustomEmailCategoryPreference: ({ customEmailId, categoryKey, enabled }) =>
    axios.patch(`/notifications/custom-emails/${customEmailId}/category/${encodeURIComponent(categoryKey)}`, { enabled })
};

export const gocardlessAPI = {
  createMandate: (ticketId, data = {}) => axios.post(`/gocardless/mandate/${ticketId}`, data),
  getMandateStatus: (userId) => axios.get(`/gocardless/mandate/status/${userId}`),
  getMandateStatusByTicket: (ticketId) => axios.get(`/gocardless/mandate/status-by-ticket/${ticketId}`),
  completeMandate: (redirectFlowId) => axios.get(`/gocardless/mandate/complete/${redirectFlowId}`),
  processManualPayment: (userId, amount, description) => axios.post(`/gocardless/payments/manual/${userId}`, { amount, description }),
  getFailureScenarios: () => axios.get(`/gocardless/payments/failure-scenarios`),
  processTestFailurePayment: (userId, scenarioType, description) => axios.post(`/gocardless/payments/test-failure/${userId}`, { scenarioType, description }),
  runBillingCycle: ({ asOf, cycleDays, userId } = {}) => axios.post('/gocardless/payments/run-cycle', { asOf, cycleDays, userId }),
  getUserPayments: (userId) => axios.get(`/gocardless/payments/user/${userId}`),
  getAdminPayments: ({ page, pageSize, status } = {}, config = {}) =>
    axios.get('/gocardless/payments/admin', {
      params: {
        page,
        pageSize,
        status,
      },
      ...config,
    }),
  // Keep compatibility with existing admin dashboard call sites.
  getAdminPaiements: ({ page, pageSize, status } = {}, config = {}) =>
    axios.get('/gocardless/payments/admin', {
      params: {
        page,
        pageSize,
        status,
      },
      ...config,
    }),
  clearUserPayments: (userId) => axios.delete(`/gocardless/payments/user/${userId}`)
};

export const docusignAPI = {
  sendEnvelope: ({ ticketId, templateId, roleName }) =>
    axios.post('/docusign/envelopes/send', { ticketId, templateId, roleName }),
  getLatestEnvelope: (ticketId) =>
    axios.get('/docusign/envelopes/latest', { params: { ticketId, refresh: 1 } }),
  sendBundle: ({ ticketId, templateIdContract1, templateIdContract2, templateIdContract3, guardianEmail, adminEmail, adminData }) =>
    axios.post('/docusign/envelopes/send-bundle', { ticketId, templateIdContract1, templateIdContract2, templateIdContract3, guardianEmail, adminEmail, adminData })
};

export const invoicesAPI = {
  listMyInvoices: (params, config = {}) => axios.get('/invoices', { params, ...config }),
  getMyInvoice: (id, config = {}) => axios.get(`/invoices/${id}`, config),
  getMyInvoicePdf: (id, config = {}) => axios.get(`/invoices/${id}/pdf`, config),
  listAdminInvoices: (params, config = {}) => axios.get('/invoices/admin/list', { params, ...config }),
  getAdminInvoice: (id, config = {}) => axios.get(`/invoices/admin/${id}`, config),
  getAdminInvoicePdf: (id, config = {}) => axios.get(`/invoices/admin/${id}/pdf`, config),
  getNotificationTemplates: (config = {}) => axios.get('/invoices/admin/notification-templates', config),
  updateNotificationTemplate: (templateKey, data, config = {}) =>
    axios.put(`/invoices/admin/notification-templates/${encodeURIComponent(templateKey)}`, data, config)
};

export const teamAPI = {
  getYears: () => axios.get('/team/years'),
  getEmployeesForYear: (year) => axios.get('/team', { params: { year } }),
  createEmployee: (formData) => axios.post('/team', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteEmployee: (id) => axios.delete(`/team/${id}`),
  listAdminAccounts: () => axios.get('/team/admin-accounts'),
  createAdminAccount: (data) => axios.post('/team/admin-accounts', data),
  deleteAdminAccount: (id) => axios.delete(`/team/admin-accounts/${id}`),
  listAdminRoleTypes: () => axios.get('/team/admin-role-types'),
  createAdminRoleType: (data) => axios.post('/team/admin-role-types', data),
  updateAdminRoleType: (id, data) => axios.put(`/team/admin-role-types/${id}`, data),
};

export const teamMembersAPI = {
  list: (config = {}) => axios.get('/team-member', config),
};

export const documentRequestsAPI = {
  // User
  create: (data) => axios.post('/document-requests', data),
  listMine: (params, config = {}) => axios.get('/document-requests', { params, ...config }),

  // Admin
  listAdmin: (params, config = {}) => axios.get('/document-requests/admin/list', { params, ...config }),
  getAdmin: (id, config = {}) => axios.get(`/document-requests/admin/${id}`, config),
  uploadAdminDoc: ({ id, docType, formData }) =>
    axios.post(`/document-requests/admin/${id}/upload/${encodeURIComponent(docType)}`, formData),
  confirmAdmin: (id) => axios.post(`/document-requests/admin/${id}/confirm`),
};

export const contactMessagesAPI = {
  create: (data) => axios.post('/contact-messages', data),
  listAdmin: (params, config = {}) => axios.get('/contact-messages/admin/list', { params, ...config }),
  getAdminUnreadCount: (config = {}) => axios.get('/contact-messages/admin/unread-count', config),
  markSeen: (id) => axios.patch(`/contact-messages/admin/${id}/seen`),
  respond: (id, response_message) => axios.post(`/contact-messages/admin/${id}/respond`, { response_message }),
};

export const houseManagementAPI = {
  createChangeResidenceRequest: (data) => axios.post('/house-management/change-residence', data),
  createLeaveResidenceRequest: (data) => axios.post('/house-management/leave-residence', data),
  listMine: (params, config = {}) => axios.get('/house-management/mine', { params, ...config }),
  listAdmin: (params, config = {}) => axios.get('/house-management/admin/list', { params, ...config }),
  getAdmin: (id, config = {}) => axios.get(`/house-management/admin/${id}`, config),
  markAdminSeen: (id) => axios.post(`/house-management/admin/${id}/seen`),
};

export const carouselAPI = {
  listPublicSlides: (params, config = {}) => axios.get('/carousel/slides', { params, ...config }),
  listAdminSlides: (params, config = {}) => axios.get('/carousel/admin/slides', { params, ...config }),
  createSlide: (formData) => axios.post('/carousel/admin/slides', formData),
  updateSlide: (id, formData) => axios.put(`/carousel/admin/slides/${id}`, formData),
  deleteSlide: (id) => axios.delete(`/carousel/admin/slides/${id}`),
  reorderSlides: (ordered_ids) => axios.patch('/carousel/admin/slides/reorder', { ordered_ids }),
};

export const footerAPI = {
  getPublic: (config = {}) => axios.get('/footer', config),
  getAdmin: (config = {}) => axios.get('/footer/admin', config),
  updateText: (adeef_text) => axios.put('/footer/admin/text', { adeef_text }),
  uploadContactLogo: (file, config = {}) => {
    const formData = new FormData();
    formData.append('image', file);
    return axios.post('/footer/admin/contacts/upload', formData, config);
  },
  createContact: (data) => axios.post('/footer/admin/contacts', data),
  updateContact: (id, data) => axios.put(`/footer/admin/contacts/${id}`, data),
  toggleContactPublish: (id, is_published) => axios.patch(`/footer/admin/contacts/${id}/publish`, { is_published }),
  deleteContact: (id) => axios.delete(`/footer/admin/contacts/${id}`),
};

export const formsAPI = {
  listPublic: (config = {}) => axios.get('/forms/public', config),
  getPublicBySlug: (slug, config = {}) => axios.get(`/forms/public/${encodeURIComponent(slug)}`, config),
  submitPublic: (slug, data, config = {}) => axios.post(`/forms/public/${encodeURIComponent(slug)}/submit`, data, config),
  getRequestStatus: ({ email, ticketCode }, config = {}) =>
    axios.get('/forms/status', {
      params: { email, ticketCode },
      ...config,
    }),

  // Admin - form definitions
  listAdminForms: (params, config = {}) => axios.get('/forms/admin/forms', { params, ...config }),
  getAdminForm: (id, config = {}) => axios.get(`/forms/admin/forms/${id}`, config),
  createAdminForm: (data, config = {}) => axios.post('/forms/admin/forms', data, config),
  updateAdminForm: (id, data, config = {}) => axios.put(`/forms/admin/forms/${id}`, data, config),
  publishAdminForm: (id, config = {}) => axios.patch(`/forms/admin/forms/${id}/publish`, {}, config),
  unpublishAdminForm: (id, config = {}) => axios.patch(`/forms/admin/forms/${id}/unpublish`, {}, config),
  deleteAdminForm: (id, config = {}) => axios.delete(`/forms/admin/forms/${id}`, config),

  // Admin - submissions
  listAdminSubmissions: (params, config = {}) => axios.get('/forms/admin/submissions', { params, ...config }),
  getAdminSubmission: (id, config = {}) => axios.get(`/forms/admin/submissions/${id}`, config),
  markAdminSubmissionSeen: (id, config = {}) => axios.post(`/forms/admin/submissions/${id}/seen`, {}, config),
  reviewAdminSubmission: (id, data, config = {}) => axios.post(`/forms/admin/submissions/${id}/review`, data, config),

  // City catalog
  getPublicCityCatalog: (config = {}) => axios.get('/forms/public/cities', config),
  getAdminCityCatalog: (config = {}) => axios.get('/forms/admin/cities', config),
  updateAdminCityCatalog: (data, config = {}) => axios.put('/forms/admin/cities', data, config),
};

export const reviewsAPI = {
  listAll: (config = {}) => axios.get('/reviews', config),
  listByDepartment: (department, config = {}) => axios.get(`/reviews/department/${encodeURIComponent(department)}`, config),
  create: (data, config = {}) => axios.post('/reviews', data, config),
  listAdmin: ({ q, limit } = {}, config = {}) => axios.get('/reviews/admin/list', { params: { q, limit }, ...config }),
  deleteAdmin: (id, config = {}) => axios.delete(`/reviews/admin/${id}`, config),
  listRegionInfoPublic: (config = {}) => axios.get('/reviews/regions-info', config),
  listRegionInfoAdmin: (config = {}) => axios.get('/reviews/admin/regions-info', config),
  saveRegionInfoAdmin: (regionKey, info_text, config = {}) =>
    axios.put(`/reviews/admin/regions-info/${encodeURIComponent(regionKey)}`, { info_text }, config),
  getFormConfig: (config = {}) => axios.get('/reviews/admin/form-config', config),
  saveFormConfig: (fields, config = {}) => axios.put('/reviews/admin/form-config', { fields }, config),
  getFormConfigPublic: (config = {}) => axios.get('/reviews/form-config', config),
};

export const emailTemplatesAPI = {
  getCategories: (config = {}) => axios.get('/email-templates/categories', config),
  listTemplates: (config = {}) => axios.get('/email-templates', config),
  getTemplate: (key, config = {}) => axios.get(`/email-templates/${encodeURIComponent(key)}`, config),
  updateTemplate: (key, data, config = {}) => axios.put(`/email-templates/${encodeURIComponent(key)}`, data, config),
};

const api = {
  auth: authAPI,
  events: eventsAPI,
  users: usersAPI,
  residences: residencesAPI,
  signup: signupAPI,
  email: emailAPI,
  notifications: notificationsAPI,
  gocardless: gocardlessAPI
  , docusign: docusignAPI
  , team: teamAPI
  , teamMembers: teamMembersAPI
  , invoices: invoicesAPI
  , documentRequests: documentRequestsAPI
  , contactMessages: contactMessagesAPI
  , houseManagement: houseManagementAPI
  , carousel: carouselAPI
  , footer: footerAPI
  , forms: formsAPI
  , reviews: reviewsAPI
  , emailTemplates: emailTemplatesAPI
};

export default api;